import React, { useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { BarLoader } from 'react-spinners';
import { ArchiveMorphologyStore } from './ArchiveMorphologyStore';
import { useFirstTimeInstructions, useIsMobileDimensions, useKeyboardInput, usePageVisibilityPause } from '../shared/utils';
import { ArchiveMorphologyData, checkPuzzle, handleInput, initArchiveMorphology, saveMorphology } from './archiveMorphologyUtils';
import { PuzzleNames } from '../../../utils/types';
import DesktopMorphology from './components/DesktopMorphology';
import MobileMorphology from './components/MobileMorphology';
import '../shared/puzzles.less';
import '../Morphology/Morphology.less';

// Build an MM-DD-YYYY key from URL params like /morphology/2025/10/3.
// Accepts non-padded month/day and zero-pads them. Returns null if the parts
// don't form a structurally valid calendar date.
const buildDateKey = (
  year?: string,
  month?: string,
  day?: string,
): string | null => {
  if (!year || !month || !day) return null;
  if (!/^\d{1,4}$/.test(year) || !/^\d{1,2}$/.test(month) || !/^\d{1,2}$/.test(day)) {
    return null;
  }
  const y = Number(year);
  const m = Number(month);
  const d = Number(day);
  if (y < 2000 || y > 9999 || m < 1 || m > 12 || d < 1 || d > 31) return null;

  const mm = String(m).padStart(2, '0');
  const dd = String(d).padStart(2, '0');
  const yyyy = String(y);

  // Reject impossible days (e.g. 02-31) by round-tripping through a UTC date.
  const probe = new Date(Date.UTC(y, m - 1, d));
  if (probe.getUTCMonth() !== m - 1 || probe.getUTCDate() !== d) return null;

  return `${mm}-${dd}-${yyyy}`;
};

// Today's date in Eastern time as MM-DD-YYYY, matching the backend's getESTDate().
// en-CA yields YYYY-MM-DD; we reorder to MM-DD-YYYY for comparison.
const getEasternTodayKey = (): string => {
  const [yyyy, mm, dd] = new Date()
    .toLocaleDateString('en-CA', { timeZone: 'America/New_York' })
    .split('-');
  return `${mm}-${dd}-${yyyy}`;
};

type LoadStatus = 'loading' | 'ready' | 'notfound';

// Inner board: identical to the daily Morphology body, but bound to the archive
// store. Rendered only once data is loaded, so the puzzle hooks below always see
// a populated store.
const ArchiveMorphologyBoard = () => {
  const { autocheckOn, guesses, completed } = ArchiveMorphologyStore.useState();

  // Mirrors the daily puzzle: morphology only uses `completed`, so we pass it for
  // both the `revealed` and `solved` params of useKeyboardInput.
  useKeyboardInput(handleInput, completed, completed);
  usePageVisibilityPause(ArchiveMorphologyStore as any, saveMorphology);
  useFirstTimeInstructions(PuzzleNames.Morphology, ArchiveMorphologyStore as any);

  useEffect(() => {
    if (autocheckOn) checkPuzzle();
  }, [guesses, autocheckOn]);

  const useMobileStyles = isMobile || useIsMobileDimensions();

  return (
    <div className={`puzzleContainer ${useMobileStyles ? 'mobile' : ''}`}>
      {useMobileStyles && <MobileMorphology/>}
      {!useMobileStyles && <DesktopMorphology/>}
    </div>
  );
};

const ArchiveMorphology = () => {
  const { year, month, day } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<LoadStatus>('loading');

  const dateKey = buildDateKey(year, month, day);
  const isToday = dateKey !== null && dateKey === getEasternTodayKey();

  useEffect(() => {
    // Reset to a clean slate before (re)loading, so navigating between archive
    // dates never flashes the previously loaded puzzle.
    setStatus('loading');

    if (!dateKey) {
      setStatus('notfound');
      return;
    }

    // If this is today's puzzle, send the user to the live daily route instead.
    if (isToday) {
      navigate('/morphology', { replace: true });
      return;
    }

    let cancelled = false;
    axios
      .get<ArchiveMorphologyData>(`/api/puzzles/morphology-archive?date=${dateKey}`)
      .then((response: { data: ArchiveMorphologyData }) => {
        if (cancelled) return;
        initArchiveMorphology(response.data);
        setStatus('ready');
      })
      .catch(() => {
        if (!cancelled) setStatus('notfound');
      });

    return () => {
      cancelled = true;
    };
  }, [dateKey, isToday, navigate]);

  if (isToday) {
    // The effect will redirect; render nothing meaningful in the meantime.
    return null;
  }

  if (status === 'notfound') {
    return <Navigate to="/" replace />;
  }

  if (status === 'loading') {
    return (
      <div className="loadingScreen">
        <BarLoader/>
      </div>
    );
  }

  return <ArchiveMorphologyBoard/>;
};

export default ArchiveMorphology;
